import { client } from '@/generated/korza-api-client/client.gen';
import { useQuery } from '@tanstack/react-query';
import { useTenant } from './use-tenant';

export type EcomProductDto = {
  ecomPlatformProductId: string;
  name: string;
  previewImageUrl: string | null;
};

export type ListTenantProductsResponseDto = {
  products: EcomProductDto[];
};

export type ProductsApiErrorCode =
  | 'NO_INTEGRATION'
  | 'UNSUPPORTED_PLATFORM'
  | 'UPSTREAM_ERROR';

export class ProductsApiError extends Error {
  constructor(
    public readonly code: ProductsApiErrorCode,
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = 'ProductsApiError';
  }
}

function isProductsApiErrorCode(value: unknown): value is ProductsApiErrorCode {
  return (
    value === 'NO_INTEGRATION' ||
    value === 'UNSUPPORTED_PLATFORM' ||
    value === 'UPSTREAM_ERROR'
  );
}

export async function fetchTenantProducts(
  tenantSlug: string
): Promise<EcomProductDto[]> {
  const res = await client.get({
    url: '/tenants/{tenantSlug}/products',
    path: { tenantSlug },
  });

  if (res.error || !res.data) {
    const body = res.error as { code?: unknown; message?: unknown } | undefined;
    const code = body?.code;
    const message =
      typeof body?.message === 'string'
        ? body.message
        : 'Failed to load products';
    const status = res.response?.status ?? 502;
    if (isProductsApiErrorCode(code)) {
      throw new ProductsApiError(code, message, status);
    }
    throw new ProductsApiError('UPSTREAM_ERROR', message, status);
  }

  const data = res.data as ListTenantProductsResponseDto;
  return data.products ?? [];
}

export function useTenantProducts() {
  const tenant = useTenant();

  return useQuery({
    queryKey: ['products', tenant.slug],
    queryFn: () => fetchTenantProducts(tenant.slug),
    enabled: !!tenant.slug,
  });
}
